// @givmo/app-charity — masked payment-profile view (CPA-WEB4).
//
// The read view of an existing payment profile. §3.4: MASKED display only — the last-4 (`•••• 1234`),
// the account-holder name, the method, and the HONEST verification status. There is deliberately NO
// read-back affordance (no "show number" button) because no read-back API exists and the full number is
// never rendered after entry. The status line NEVER implies money will flow unless the profile is
// genuinely payout-ready (see payment-vocab `isPayoutReady`) — and "verified" is not that: an ACH
// profile that passed the name match is verified the moment it is saved, while transfers are enabled
// only once the backend's payee sync has bound it (`transfers_enabled`). Between the two, the card
// shows a neutral "Verified" and states the verified fact alone. The binding DOMINATES the wire's
// flagged state (payment-vocab header: precedence): a name-mismatch profile a reviewer approved is
// bound with `flagged` still true, and it renders the green state, never the review one.

import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Icon,
  testIdProp,
} from '@givmo/design-system';
import { CheckCircle2, Landmark, Lock, Mail, ShieldAlert } from 'lucide-react';
import type { CharityPaymentProfileOut } from '@givmo/api-client';
import {
  isAwaitingOwnerBankDetails,
  isAwaitingTransferSetup,
  isCheckMethod,
  isPayoutReady,
  isUnderReview,
  maskedAccount,
  methodLabel,
  verificationDescription,
  verificationLabel,
  verificationTone,
} from './payment-vocab';

const PREFIX = 'payment-profile';

export interface MaskedProfileViewProps {
  profile: CharityPaymentProfileOut;
  /** Owner-only: change bank details (ACH) — re-verification warning is handled by the parent. */
  onChangeBank?: () => void;
  /** Owner-only: switch to a different method (ACH ⇄ check). */
  onChangeMethod?: () => void;
  /**
   * Whether the reader is the organization's Global Admin (CPA-BE69, review-33's F2). After a
   * change of ownership only a current Global Admin's own entry re-binds the bank link (D-CP-101's
   * floor), so only they read "you" and keep the controls in that state; anyone else reads that a
   * Global Admin enters the details, and the card shows them no controls whatever the parent passes.
   * Defaults to `false`, the words that are true for every reader.
   */
  readerIsGlobalAdmin?: boolean;
}

export function MaskedProfileView({
  profile,
  onChangeBank,
  onChangeMethod,
  readerIsGlobalAdmin = false,
}: MaskedProfileViewProps) {
  const check = isCheckMethod(profile.method);
  const masked = maskedAccount(profile.account_last4);
  const ready = isPayoutReady(profile);
  const underReview = isUnderReview(profile);
  const awaiting = isAwaitingTransferSetup(profile);
  // CPA-BE69: after a change of ownership the details on file are not used until a Global Admin
  // enters the organization's own. The note says grants go by mailed check (the mail icon). For the
  // Global Admin the bank button becomes the one thing to do, "Enter bank details", primary; anyone
  // else gets no controls in this state, since their entry would never lift it (D-CP-101's floor).
  const ownerDetailsNeeded = isAwaitingOwnerBankDetails(profile);
  const controlsWithheld = ownerDetailsNeeded && !readerIsGlobalAdmin;

  // Five presentations, matching the five things that can actually be true: settled (ready), verified
  // but transfers not yet enabled (awaiting — the neutral one: a check mark, but no green, because the
  // green surface is the money-will-flow claim), waiting for a current Owner's bank details after a
  // change of ownership (warning, with the mail icon), under human review, or a state this bundle
  // doesn't recognize (warning, and never ready). READY IS CHECKED FIRST: an approved name-mismatch profile
  // is flagged on the wire and enabled, and `isUnderReview` (not the raw flag) is already false for
  // it — the ordering here is the second lock on the same door.
  const StatusIcon = ready || awaiting ? CheckCircle2 : ownerDetailsNeeded ? Mail : ShieldAlert;
  const statusSurface = ready
    ? 'border-success-100 bg-success-50'
    : underReview
      ? 'border-danger-100 bg-danger-50'
      : awaiting
        ? 'border-border-subtle bg-surface-sunken'
        : 'border-warning-100 bg-warning-50';
  const statusIconColor = ready
    ? 'text-success-600'
    : underReview
      ? 'text-danger-600'
      : awaiting
        ? 'text-text-tertiary'
        : 'text-warning-600';

  return (
    <Card className="overflow-hidden" testId={`${PREFIX}-card`}>
      <CardHeader className="px-24 py-20">
        {/* Stacked below `sm`, the estate's `PageHeader` rule (components/page-header.tsx). The
            verification Badge is `whitespace-nowrap` and the method title beside it does not shrink
            far enough to pay for it: at 320px the row measured 280px inside a 230px lane, and this
            Card is `overflow-hidden`, so ~25px of a 69px "Verified" badge was CLIPPED — the one line
            that tells an admin whether their grants can actually be paid, cut mid-word with no
            ellipsis, and invisible to a page-level scrollWidth check because the Card swallowed it.
            From `sm` up this is `flex-row` + `items-center` + `justify-between` + `gap-12` again,
            i.e. the row as it was. */}
        <div className="flex flex-col gap-12 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-12">
            <span className="flex size-[40px] shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <Icon icon={check ? Mail : Landmark} size={20} aria-hidden />
            </span>
            <div className="min-w-0">
              {/* Not "current" while the details on file are not used (CPA-BE69): the note below
                  says grants go by mailed check meanwhile. */}
              <p className="text-s11 font-w600 uppercase tracking-wide text-text-tertiary">
                {ownerDetailsNeeded ? 'Payment method on file' : 'Current payment method'}
              </p>
              <h2
                className="mt-2 truncate text-s17 font-w700 text-text-strong"
                {...testIdProp(`${PREFIX}-method`)}
              >
                {methodLabel(profile.method)}
              </h2>
            </div>
          </div>
          <span {...testIdProp(`${PREFIX}-status`)}>
            <Badge tone={verificationTone(profile)} size="md" dot>
              {verificationLabel(profile)}
            </Badge>
          </span>
        </div>
      </CardHeader>
      <CardBody className="flex flex-col gap-20 px-24 py-24">
        {/* Honest payout-readiness explanation. Never claims money will flow unless truly ready. */}
        <div
          className={`flex items-start gap-10 rounded-lg border p-14 ${statusSurface}`}
          {...testIdProp(`${PREFIX}-status-note`)}
        >
          <Icon
            icon={StatusIcon}
            size={18}
            className={`mt-1 shrink-0 ${statusIconColor}`}
            aria-hidden
          />
          <p className="text-s13 text-text-secondary">
            {verificationDescription(profile, { isGlobalAdmin: readerIsGlobalAdmin })}
          </p>
        </div>

        {/* The masked details. Only the last-4 is ever shown for a bank account. */}
        {profile.account_holder_name || masked || profile.bank_name ? (
          <div className="rounded-xl border border-border bg-surface-sunken p-16">
            <h3 className="text-s12 font-w600 uppercase tracking-wide text-text-tertiary">
              {check ? 'Delivery details' : 'Account details'}
            </h3>
            <dl className="mt-14 grid grid-cols-1 gap-14 border-t border-border-subtle pt-14 sm:grid-cols-2">
              {profile.account_holder_name ? (
                <div>
                  <dt className="text-s12 text-text-tertiary">Account holder</dt>
                  <dd
                    className="mt-2 font-w500 text-text-strong"
                    {...testIdProp(`${PREFIX}-holder`)}
                  >
                    {profile.account_holder_name}
                  </dd>
                </div>
              ) : null}
              {masked ? (
                <div>
                  <dt className="text-s12 text-text-tertiary">Account number</dt>
                  <dd
                    className="mt-2 font-w500 tabular-nums text-text-strong"
                    {...testIdProp(`${PREFIX}-mask`)}
                  >
                    {masked}
                  </dd>
                </div>
              ) : null}
              {profile.bank_name ? (
                <div>
                  <dt className="text-s12 text-text-tertiary">Bank</dt>
                  <dd className="mt-2 font-w500 text-text-strong" {...testIdProp(`${PREFIX}-bank`)}>
                    {profile.bank_name}
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        ) : null}

        {!check ? (
          <div className="flex items-center gap-8 text-s12 text-text-tertiary">
            <Icon icon={Lock} size={14} className="shrink-0" aria-hidden />
            <p>Only the last 4 digits of your account are visible in the portal.</p>
          </div>
        ) : null}
      </CardBody>
      {/* Owner-only management affordances. Non-owners never get these (parent gates on role), and
          after a change of ownership only the Global Admin does (the card gates that itself). */}
      {(onChangeBank || onChangeMethod) && !controlsWithheld ? (
        <CardFooter className="flex-col-reverse items-stretch px-24 py-16 sm:flex-row sm:items-center sm:justify-end">
          {onChangeMethod ? (
            <Button
              appearance="saas"
              tone="ghost"
              size="sm"
              onClick={onChangeMethod}
              testId={`${PREFIX}-change-method`}
            >
              Use a different payment method
            </Button>
          ) : null}
          {onChangeBank && !check ? (
            <Button
              appearance="saas"
              tone={ownerDetailsNeeded ? 'primary' : 'secondary'}
              size="sm"
              onClick={onChangeBank}
              testId={`${PREFIX}-change-bank`}
            >
              {ownerDetailsNeeded ? 'Enter bank details' : 'Change bank details'}
            </Button>
          ) : null}
        </CardFooter>
      ) : null}
    </Card>
  );
}
